import JSZip from 'jszip';

export type ClientsImportTemplateLogo = {
    bytes: Uint8Array;
    width: number;
    height: number;
};

// Decode the fetched logo while sharing the download's timeout and cleanup.
function decodeLogo(url: string, signal: AbortSignal): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const cleanup = () => {
            image.onload = null;
            image.onerror = null;
            signal.removeEventListener('abort', abort);
        };
        const abort = () => {
            cleanup();
            reject(new Error('The provider logo could not be loaded.'));
        };
        image.onload = () => {
            cleanup();
            resolve(image);
        };
        image.onerror = abort;
        signal.addEventListener('abort', abort, { once: true });
        if (signal.aborted) {
            abort();
        } else {
            image.src = url;
        }
    });
}

// Rasterize optional provider images, including SVG logos, into a portable PNG for row 1.
export async function loadClientsImportTemplateLogo(logoUrl?: string): Promise<ClientsImportTemplateLogo | null> {
    const sourceUrl = logoUrl?.trim();
    if (!sourceUrl) {
        return null;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    let objectUrl: string | undefined;
    let stage = 'download';
    try {
        // Avoid reusing an image-tag response cached without cross-origin download headers.
        const response = await fetch(sourceUrl, { credentials: 'omit', cache: 'no-store', signal: controller.signal });
        if (!response.ok) {
            throw new Error('The provider logo request failed.');
        }
        objectUrl = URL.createObjectURL(await response.blob());
        stage = 'image decode';
        const image = await decodeLogo(objectUrl, controller.signal);
        if (!image.naturalWidth || !image.naturalHeight) {
            return null;
        }
        const scale = Math.min(152 / image.naturalWidth, 72 / image.naturalHeight, 1);
        const width = Math.max(1, Math.round(image.naturalWidth * scale));
        const height = Math.max(1, Math.round(image.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        stage = 'PNG conversion';
        canvas.width = width * 2;
        canvas.height = height * 2;
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        return png ? { bytes: new Uint8Array(await png.arrayBuffer()), width, height } : null;
    } catch {
        // An unavailable optional logo must not prevent downloading a usable import template.
        // Log only the failed stage; download URLs can contain private access tokens.
        console.warn('CLIENTS_IMPORT: Provider logo omitted because loading failed.', { stage });
        return null;
    } finally {
        window.clearTimeout(timeout);
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
    }
}

// Embed the logo entirely inside A1 without placing text or images in the import headers.
export async function addClientsImportTemplateLogo(zip: JSZip, logo: ClientsImportTemplateLogo, maxWidth = 152): Promise<void> {
    const drawingNamespace = 'http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing';
    const relationshipsNamespace = 'http://schemas.openxmlformats.org/package/2006/relationships';
    const officeRelationships = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
    // Resize the drawing proportionally while retaining the original PNG's resolution.
    const scale = Math.min(1, maxWidth / logo.width);
    const displayWidth = Math.max(1, Math.round(logo.width * scale));
    const displayHeight = Math.max(1, Math.round(logo.height * scale));
    const widthEmu = displayWidth * 9525;
    const heightEmu = displayHeight * 9525;
    const topEmu = Math.round((96 - displayHeight) / 2) * 9525;

    zip.file('xl/media/provider-logo.png', logo.bytes);
    zip.file('xl/drawings/drawing1.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="${drawingNamespace}" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
<xdr:oneCellAnchor><xdr:from><xdr:col>0</xdr:col><xdr:colOff>76200</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>${topEmu}</xdr:rowOff></xdr:from><xdr:ext cx="${widthEmu}" cy="${heightEmu}"/>
<xdr:pic><xdr:nvPicPr><xdr:cNvPr id="1" name="Healthcare provider logo"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr><xdr:blipFill><a:blip xmlns:r="${officeRelationships}" r:embed="rIdLogo"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill><xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${widthEmu}" cy="${heightEmu}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>`);
    zip.file('xl/drawings/_rels/drawing1.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="${relationshipsNamespace}"><Relationship Id="rIdLogo" Type="${officeRelationships}/image" Target="../media/provider-logo.png"/></Relationships>`);
    zip.file('xl/worksheets/_rels/sheet1.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="${relationshipsNamespace}"><Relationship Id="rIdProviderLogo" Type="${officeRelationships}/drawing" Target="../drawings/drawing1.xml"/></Relationships>`);

    const worksheet = await zip.file('xl/worksheets/sheet1.xml')!.async('string');
    zip.file('xl/worksheets/sheet1.xml', worksheet.replace('</worksheet>', '<drawing r:id="rIdProviderLogo"/></worksheet>'));
    const contentTypes = await zip.file('[Content_Types].xml')!.async('string');
    zip.file('[Content_Types].xml', contentTypes.replace('</Types>', '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/><Override PartName="/xl/media/provider-logo.png" ContentType="image/png"/></Types>'));
}
